import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Search,
  Eye
} from 'lucide-react';
import { hostGuestsService } from '../../services/hostGuestsService';
import { 
  HostGuest, 
  HostGuestListParams,
  HostEventOption 
} from '../../types/hostGuest';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import Button from '../../components/UI/Button';
import Pagination from '../../components/UI/Pagination';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import HostGuestViewModal from '../../components/guests/HostGuestViewModal';

const HostGuestsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { theme } = useAppSelector((state) => state.ui);
  
  // State management
  const [guests, setGuests] = useState<HostGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [events, setEvents] = useState<HostEventOption[]>([]);
  
  // Modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState<HostGuest | null>(null);

  // Load guests data
  const loadGuests = useCallback(async (params: HostGuestListParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query params - use params values if provided, otherwise defaults
      const queryParams: HostGuestListParams = {
        page: params.page ?? currentPage,
        page_size: params.page_size ?? pageSize,
        search: params.search,
        event_id: params.event_id
      };
      
      const response = await hostGuestsService.getGuests(queryParams);
      
      if (response.success) {
        setGuests(response.data.guests);
        setTotalPages(response.data.pagination.total_pages);
        setTotalCount(response.data.pagination.total_count);
        // Update events list for filter (only on initial load or when not filtered)
        if (response.data.events && response.data.events.length > 0) {
          setEvents(response.data.events);
        }
      } else {
        setError('Failed to load guests');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load guests');
      showNotification('Error loading guests', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, showNotification]);

  // Handle search and filter changes - reset to page 1
  useEffect(() => {
    // Reset to page 1 when search or filter changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      return; // The page change will trigger a reload via the next effect
    }
    
    const timeoutId = setTimeout(() => {
      const params: HostGuestListParams = {
        page: 1,
        page_size: pageSize,
        search: searchTerm || undefined,
        event_id: selectedEventId || undefined
      };
      loadGuests(params);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedEventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle pagination changes
  useEffect(() => {
    // Skip if it's the initial render (page 1) - the search effect handles that
    if (currentPage === 1) return;
    
    const params: HostGuestListParams = {
      page: currentPage,
      page_size: pageSize,
      search: searchTerm || undefined,
      event_id: selectedEventId || undefined
    };
    loadGuests(params);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle view guest
  const handleViewGuest = (guest: HostGuest) => {
    setSelectedGuest(guest);
    setIsViewModalOpen(true);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedEventId('');
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm.trim() !== '' || selectedEventId !== '';

  if (loading && guests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              My phone book
            </h1>
            <p className={`mt-1 text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              View all guests across your events
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar and Event Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search Input */}
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search by guest name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`block w-full rounded-md shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm transition-all duration-200 pl-10 pr-3 py-2.5 ${
              theme === 'dark'
                ? `border-gray-600 bg-gray-800 text-white placeholder-gray-400`
                : `border-gray-300 bg-white text-gray-900 placeholder-gray-500`
            }`}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
          </div>
        </div>
        
        {/* Event Filter Dropdown */}
        <div className="sm:w-64">
          <select
            value={selectedEventId}
            onChange={(e) => {
              setSelectedEventId(e.target.value);
              setCurrentPage(1);
            }}
            className={`block w-full rounded-md shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm transition-all duration-200 px-3 py-2.5 ${
              theme === 'dark'
                ? `border-gray-600 bg-gray-800 text-white`
                : `border-gray-300 bg-white text-gray-900`
            }`}
          >
            <option value="">All Events</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className={`rounded-lg p-2 backdrop-blur-sm ${
        theme === 'dark' 
          ? 'bg-gray-900/80 border border-gray-700' 
          : 'bg-white/90 border border-gray-200'
      }`}>

        {/* Error State */}
        {error && (
          <div className={`rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 ${
            theme === 'dark' 
              ? 'bg-red-900/20 border border-red-700/50' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center">
              <div className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>
                <p className="text-sm sm:text-base font-medium">Error loading guests</p>
                <p className="text-xs sm:text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Guests Table */}
        {guests.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  <th scope="col" className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Guest
                  </th>
                  <th scope="col" className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Phone
                  </th>
                  <th scope="col" className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Companions
                  </th>
                  <th scope="col" className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {guests.map((guest) => (
                  <tr 
                    key={guest.id}
                    className={`${theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`h-10 w-10 flex-shrink-0 rounded-full flex items-center justify-center ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                          <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {guest.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {guest.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {guest.phone || '-'}
                      </p>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {guest.companion_count}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewGuest(guest)}
                        className={theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <Users className={`mx-auto h-10 w-10 sm:h-12 sm:w-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
            <h3 className={`mt-2 text-sm sm:text-base font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              No guests found
            </h3>
            <p className={`mt-1 text-xs sm:text-sm px-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
              {hasActiveFilters
                ? 'Try adjusting your filters or search terms.'
                : 'Your guests will appear here once you add them to your events.'
              }
            </p>
            {hasActiveFilters && (
              <div className="mt-4">
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalCount={totalCount}
              pageSize={pageSize}
            />
          </div>
        )}
      </div>

      {/* View Guest Modal */}
      {selectedGuest && (
        <HostGuestViewModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedGuest(null);
          }}
          guest={selectedGuest}
        />
      )}
    </div>
  );
};

export default HostGuestsPage;
